import React, { useState, useContext } from 'react';
import { supabase } from '../supabase/client';
import NotificationBell from './shared/NotificationBell';
import { CSRContextBanner } from './csr/CSRContextBanner';
import { AuthSessionContext } from '../contexts/AuthSessionContext';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCampaignsDropdownOpen, setIsCampaignsDropdownOpen] = useState(false);
  // isLeadGenDropdownOpen removed — Lead Generator nav link removed (accessible via campaign wizard)
  const [isAdminDropdownOpen, setIsAdminDropdownOpen] = useState(false);
  const [isCSRDropdownOpen, setIsCSRDropdownOpen] = useState(false);

  // Consume session and userRole from AuthSessionContext
  // This eliminates the redundant DB query and onAuthStateChange listener
  const { session, userRole } = useContext(AuthSessionContext);

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Error logging out:', error);
      } else {
        window.location.href = '/login';
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <>
      <CSRContextBanner />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo/Brand */}
          <div className="flex items-center">
            <a href="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">SE</span>
              </div>
              <span className="text-xl font-bold text-gray-900">Standing Encore</span>
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <a 
              href="/"
              className="text-gray-700 hover:text-purple-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
            >
              🏠 Home
            </a>
            
            {/* Campaigns Dropdown - Hidden for Admin and CSR users */}
            {userRole !== 'admin' && userRole !== 'csr' && (
              <div className="relative">
                <button
                  onClick={() => setIsCampaignsDropdownOpen(!isCampaignsDropdownOpen)}
                  className="text-gray-700 hover:text-purple-600 px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center"
                >
                  📋 Campaigns
                  <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {isCampaignsDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-10">
                    <a
                      href="/leads-inbox"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsCampaignsDropdownOpen(false)}
                    >
                      Leads Inbox
                    </a>
                    <a
                      href="/campaigns"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsCampaignsDropdownOpen(false)}
                    >
                      My Campaigns
                    </a>
                    <a
                      href="/create-campaign"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsCampaignsDropdownOpen(false)}
                    >
                      Create Campaign
                    </a>
                    <a
                      href="/reports"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsCampaignsDropdownOpen(false)}
                    >
                      Intelligence Reports
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* Admin Panel Dropdown - Only for Admin users */}
            {userRole === 'admin' && (
              <div className="relative">
                <button
                  onClick={() => setIsAdminDropdownOpen(!isAdminDropdownOpen)}
                  className="text-gray-700 hover:text-purple-600 px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center"
                >
                  ⚙️ Admin
                  <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {isAdminDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-10">
                    <a
                      href="/admin"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 font-medium border-b border-gray-100"
                      onClick={() => setIsAdminDropdownOpen(false)}
                    >
                      🛡️ Admin Panel Dashboard
                    </a>
                    <a
                      href="/admin?tab=1"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsAdminDropdownOpen(false)}
                    >
                      👥 User Management
                    </a>
                    <a
                      href="/admin?tab=2"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsAdminDropdownOpen(false)}
                    >
                      📋 CSR Assignment
                    </a>
                    <a
                      href="/admin?tab=3"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsAdminDropdownOpen(false)}
                    >
                      🏢 Company Management
                    </a>
                    <a
                      href="/admin?tab=4"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsAdminDropdownOpen(false)}
                    >
                      💳 Quota Management
                    </a>
                    <a
                      href="/admin?tab=8"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsAdminDropdownOpen(false)}
                    >
                      📊 Demo Requests
                    </a>
                    <div className="border-t border-gray-100 mt-1 pt-1">
                      <a
                        href="/admin?tab=5"
                        className="block px-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
                        onClick={() => setIsAdminDropdownOpen(false)}
                      >
                        🔧 Preset Filters
                      </a>
                      <a
                        href="/admin?tab=6"
                        className="block px-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
                        onClick={() => setIsAdminDropdownOpen(false)}
                      >
                        💰 Pricing Configuration
                      </a>
                      <a
                        href="/admin?tab=7"
                        className="block px-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
                        onClick={() => setIsAdminDropdownOpen(false)}
                      >
                        👤 Outreach Team
                      </a>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* CSR Management Dropdown - Only for CSR users */}
            {userRole === 'csr' && (
              <div className="relative">
                <button
                  onClick={() => setIsCSRDropdownOpen(!isCSRDropdownOpen)}
                  className="text-gray-700 hover:text-purple-600 px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center"
                >
                  🎯 CSR Management
                  <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {isCSRDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-10">
                    <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
                      Campaign Management
                    </div>
                    <a
                      href="/campaign-approvals"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsCSRDropdownOpen(false)}
                    >
                      ✅ Campaign Approvals
                    </a>
                    <a
                      href="/csr-campaigns"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsCSRDropdownOpen(false)}
                    >
                      📊 My Assigned Campaigns
                    </a>
                    <a
                      href="/create-campaign"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsCSRDropdownOpen(false)}
                    >
                      🎭 Create Campaign for User
                    </a>
                    <a
                      href="/csr-filter-requests"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsCSRDropdownOpen(false)}
                    >
                      🎯 Target Lead Requests
                    </a>
                    
                    <div className="border-t border-gray-100 mt-1 pt-1">
                      <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Lead Responses
                      </div>
                      <a
                        href="/leads-inbox/add?mode=new"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsCSRDropdownOpen(false)}
                      >
                        ➕ Add Lead Response
                      </a>
                      <a
                        href="/leads-inbox"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsCSRDropdownOpen(false)}
                      >
                        📥 My Lead Responses
                      </a>
                    </div>
                    
                    <div className="border-t border-gray-100 mt-1 pt-1">
                      <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Client Management
                      </div>
                      <a
                        href="/csr-companies"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsCSRDropdownOpen(false)}
                      >
                        🏢 My Assigned Companies
                      </a>
                      <a
                        href="/csr-users"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsCSRDropdownOpen(false)}
                      >
                        👥 My Assigned Users
                      </a>
                      <a
                        href="/csr-record-usage"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsCSRDropdownOpen(false)}
                      >
                        💳 Record Outreach Usage
                      </a>
                      <a
                        href="/csr-group-config"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsCSRDropdownOpen(false)}
                      >
                        📊 Campaign Group Setup
                      </a>
                      <a
                        href="/csr-reports"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsCSRDropdownOpen(false)}
                      >
                        📋 Weekly Reports
                      </a>
                      <a
                        href="/csr-icp-signal"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsCSRDropdownOpen(false)}
                      >
                        🔬 ICP Signal Classifier
                      </a>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Notification Bell for all authenticated users */}
            {session && <NotificationBell userRole={userRole || undefined} />}

            <a 
              href="/profile"
              className="text-gray-700 hover:text-purple-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
            >
              👤 Profile
            </a>

            <button
              onClick={handleLogout}
              className="text-gray-700 hover:text-red-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
            >
              🔒 Logout
            </button>
          </nav>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 hover:text-purple-600 p-2"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-gray-200 py-2">
            <div className="space-y-1">
              <a 
                href="/"
                className="block px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                onClick={() => setIsMenuOpen(false)}
              >
                🏠 Home
              </a>



              {/* Mobile Campaigns Section - Hidden for Admin and CSR */}
              {userRole !== 'admin' && userRole !== 'csr' && (
                <div className="px-3 py-2">
                  <div className="text-sm font-medium text-gray-900 mb-2">📋 Campaigns</div>
                  <a
                    href="/leads-inbox"
                    className="block px-4 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Leads Inbox
                  </a>
                  <a
                    href="/campaigns"
                    className="block px-4 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    My Campaigns
                  </a>
                  <a
                    href="/create-campaign"
                    className="block px-4 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Create Campaign
                  </a>
                  <a
                    href="/reports"
                    className="block px-4 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Intelligence Reports
                  </a>
                </div>
              )}

              {/* Mobile Admin Section - Only for Admin */}
              {userRole === 'admin' && (
                <div className="px-3 py-2">
                  <div className="text-sm font-medium text-gray-900 mb-2">⚙️ Admin Panel</div>
                  <a
                    href="/admin"
                    className="block px-4 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded font-medium"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    🛡️ Dashboard
                  </a>
                  <a
                    href="/admin?tab=1"
                    className="block px-4 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    👥 User Management
                  </a>
                  <a
                    href="/admin?tab=2"
                    className="block px-4 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    📋 CSR Assignment
                  </a>
                  <a
                    href="/admin?tab=3"
                    className="block px-4 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    🏢 Company Management
                  </a>
                  <a
                    href="/admin?tab=4"
                    className="block px-4 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    💳 Quota Management
                  </a>
                  <a
                    href="/admin?tab=8"
                    className="block px-4 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    📊 Demo Requests
                  </a>
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <a
                      href="/admin?tab=5"
                      className="block px-4 py-1 text-sm text-gray-500 hover:bg-gray-100 rounded"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      🔧 Preset Filters
                    </a>
                    <a
                      href="/admin?tab=6"
                      className="block px-4 py-1 text-sm text-gray-500 hover:bg-gray-100 rounded"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      💰 Pricing Config
                    </a>
                    <a
                      href="/admin?tab=7"
                      className="block px-4 py-1 text-sm text-gray-500 hover:bg-gray-100 rounded"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      👤 Outreach Team
                    </a>
                  </div>
                </div>
              )}

              {/* Mobile CSR Section - Only for CSR */}
              {userRole === 'csr' && (
                <div className="px-3 py-2">
                  <div className="text-sm font-medium text-gray-900 mb-2">🎯 CSR Management</div>
                  
                  {/* Campaign Management */}
                  <div className="mb-3">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 mb-1">
                      Campaign Management
                    </div>
                    <a
                      href="/campaign-approvals"
                      className="block px-4 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      ✅ Campaign Approvals
                    </a>
                    <a
                      href="/csr-dashboard"
                      className="block px-4 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      📊 My Assigned Campaigns
                    </a>
                    <a
                      href="/csr-filter-requests"
                      className="block px-4 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      🎯 Target Lead Requests
                    </a>
                  </div>
                  
                  {/* Lead Responses */}
                  <div className="mb-3">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 mb-1">
                      Lead Responses
                    </div>
                    <a
                      href="/leads-inbox/add?mode=new"
                      className="block px-4 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      ➕ Add Lead Response
                    </a>
                    <a
                      href="/leads-inbox"
                      className="block px-4 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      📥 My Lead Responses
                    </a>
                  </div>
                  
                  {/* Client Management */}
                  <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 mb-1">
                      Client Management
                    </div>
                    <a
                      href="/csr-companies"
                      className="block px-4 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      🏢 My Assigned Companies
                    </a>
                    <a
                      href="/csr-users"
                      className="block px-4 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      👥 My Assigned Users
                    </a>
                    <a
                      href="/csr-record-usage"
                      className="block px-4 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      💳 Record Outreach Usage
                    </a>
                    <a
                      href="/csr-group-config"
                      className="block px-4 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      📊 Campaign Group Setup
                    </a>
                    <a
                      href="/csr-reports"
                      className="block px-4 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      📋 Weekly Reports
                    </a>
                    <a
                      href="/csr-icp-signal"
                      className="block px-4 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      🔬 ICP Signal Classifier
                    </a>
                  </div>
                </div>
              )}

              {/* Mobile Notification Bell */}
              {session && (
                <div className="px-3 py-2">
                  <NotificationBell userRole={userRole || undefined} />
                </div>
              )}

              <a 
                href="/profile"
                className="block px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                onClick={() => setIsMenuOpen(false)}
              >
                👤 Profile
              </a>

              <button
                onClick={() => {
                  setIsMenuOpen(false);
                  handleLogout();
                }}
                className="block w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
              >
                🔒 Logout
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
    </>
  );
};

export default Header;